import { buildCitationHTML, buildRefLabel } from './MentionAutocomplete';

// Citation pills travel through the clipboard twice over: as the chip itself in
// text/html, and as their [doc_id_N] / [ar_id_N] token in text/plain. The token is what
// a plain textarea (subtitles, footers) keeps, and what renders back as a pill.
export const CITATION_PILL_SELECTOR = '.ev-message-card6, .ev-message-card7';

const TOKEN_RE = /\[(doc_id|ar_id)_(\d+)\]/g;
const SINGLE_TOKEN_RE = /^\[(?:doc_id|ar_id)_\d+\]$/;
const BLOCK_TAG_RE = /^(P|DIV|LI|TR|H[1-6]|BLOCKQUOTE|UL|OL|TABLE)$/;

export const hasCitationToken = (text) => /\[(?:doc_id|ar_id)_\d+\]/.test(text || '');

const escapeHTML = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

const pillItem = (el) => ({
  kind: el.classList.contains('ev-message-card7') ? 'ref' : 'doc',
  // Table and figure pills show a shortened label; the title keeps the full name.
  label: el.getAttribute('title') || el.textContent || '',
  ref: el.getAttribute('data-ref') || '',
});

const tokenItem = (type, id, token, docs, refs) => {
  if (type === 'doc_id') {
    const doc = docs.find((d) => Number(d.id) === Number(id));
    return { kind: 'doc', label: doc?.name || `Document ${id}`, ref: token };
  }
  const ref = refs.find((r) => Number(r.ar_id) === Number(id));
  return { kind: 'ref', label: ref ? buildRefLabel(ref) : `Reference ${id}`, ref: token };
};

const pillHTML = (item, editable) => {
  const html = buildCitationHTML({ ...item, label: escapeHTML(item.label) });
  return editable ? html : html.replace('<span ', '<span contenteditable="false" ');
};

// Text and pills only: other formatting is left behind, line breaks are kept.
const serialize = (root, { editable = true } = {}) => {
  let text = '';
  let html = '';
  const lineBreak = () => {
    if (text && !text.endsWith('\n')) {
      text += '\n';
      html += '<br>';
    }
  };
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.nodeValue;
      html += escapeHTML(node.nodeValue);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(CITATION_PILL_SELECTOR)) {
      const item = pillItem(node);
      text += SINGLE_TOKEN_RE.test(item.ref) ? item.ref : item.label;
      html += pillHTML(item, editable);
      return;
    }
    if (node.tagName === 'BR') {
      text += '\n';
      html += '<br>';
      return;
    }
    const block = BLOCK_TAG_RE.test(node.tagName);
    if (block) lineBreak();
    node.childNodes.forEach(walk);
    if (block) lineBreak();
  };
  root.childNodes.forEach(walk);
  while (text.endsWith('\n')) {
    text = text.slice(0, -1);
    html = html.replace(/<br>$/, '');
  }
  return { text, html };
};

// Plain text with tokens -> escaped HTML with a pill per token.
export const tokenTextToHTML = (text, docs = [], refs = [], { editable = true } = {}) => {
  let html = '';
  let last = 0;
  TOKEN_RE.lastIndex = 0;
  let match;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    html += escapeHTML(text.slice(last, match.index));
    html += pillHTML(tokenItem(match[1], match[2], match[0], docs, refs), editable);
    last = match.index + match[0].length;
  }
  html += escapeHTML(text.slice(last));
  return html.replace(/\r?\n/g, '<br>');
};

// Copy handler for a contentEditable area. Takes over only when the selection holds a
// pill, so ordinary copies keep the browser's own formatting.
export const copySelectedPills = (event, container) => {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !event.clipboardData) return false;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return false;
  const holder = document.createElement('div');
  holder.appendChild(range.cloneContents());
  // A selection inside a single pill clones only its text; copy the whole pill.
  const inside = range.commonAncestorContainer.parentElement?.closest?.(CITATION_PILL_SELECTOR);
  if (inside && container.contains(inside)) {
    holder.textContent = '';
    holder.appendChild(inside.cloneNode(true));
  }
  if (!holder.querySelector(CITATION_PILL_SELECTOR)) return false;
  const { text, html } = serialize(holder);
  event.clipboardData.setData('text/plain', text);
  event.clipboardData.setData('text/html', html);
  event.preventDefault();
  return true;
};

// The HTML to insert for a paste that carries pills (as chips or as tokens), or null
// to leave the paste to the browser.
export const pastedPillsHTML = (clipboardData, docs = [], refs = [], { editable = true } = {}) => {
  if (!clipboardData) return null;
  const html = clipboardData.getData('text/html');
  if (html && /ev-message-card[67]/.test(html)) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (doc.body.querySelector(CITATION_PILL_SELECTOR)) return serialize(doc.body, { editable }).html;
  }
  const plain = clipboardData.getData('text/plain');
  if (!html && hasCitationToken(plain)) return tokenTextToHTML(plain, docs, refs, { editable });
  return null;
};

// True when a DOM range holds exactly one pill and nothing else (a clicked pill).
export const rangeIsSinglePill = (range) => {
  if (!range) return false;
  const { startContainer, endContainer, startOffset, endOffset } = range;
  if (startContainer === endContainer && endOffset - startOffset === 1
    && startContainer.nodeType === Node.ELEMENT_NODE) {
    const node = startContainer.childNodes[startOffset];
    if (node?.nodeType === Node.ELEMENT_NODE && node.matches(CITATION_PILL_SELECTOR)) return true;
  }
  const el = range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentElement;
  return !!el?.closest?.(CITATION_PILL_SELECTOR);
};

// A click on a non-editable pill puts the caret beside it, so Ctrl+C copies nothing.
// Select the whole pill instead.
export const selectClickedPill = (event, container) => {
  const pill = event.target?.closest?.(CITATION_PILL_SELECTOR);
  if (!pill || !container.contains(pill)) return false;
  const sel = window.getSelection();
  if (!sel) return false;
  const range = document.createRange();
  range.selectNode(pill);
  sel.removeAllRanges();
  sel.addRange(range);
  return true;
};
